import type { MembershipDto } from "@bafe/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useMemo, useState } from "react";

import { cn } from "@bafe/ui";
import { api } from "../lib/api.js";
import { presentError } from "../lib/errors.js";
import { useOrganisationStore } from "../lib/stores.js";

export function UserWorkspaceScope({
	memberships,
	children,
	emptyTitle,
	emptyDetail,
}: {
	memberships: MembershipDto[];
	children: ReactNode;
	emptyTitle: string;
	emptyDetail: string;
}) {
	const queryClient = useQueryClient();
	const organisationId = useOrganisationStore((state) => state.organisationId);
	const setOrganisationId = useOrganisationStore((state) => state.setOrganisationId);
	const [error, setError] = useState<string | null>(null);
	const [ready, setReady] = useState(false);

	const scoped = useMemo(() => memberships, [memberships]);

	const activeId = useMemo(() => {
		if (scoped.length === 0) return null;
		const match = scoped.find((item) => item.organisationId === organisationId);
		return match?.organisationId ?? scoped[0]?.organisationId ?? null;
	}, [organisationId, scoped]);

	useEffect(() => {
		if (scoped.length === 0) {
			setReady(true);
			return;
		}
		const target = scoped.find((item) => item.organisationId === activeId) ?? scoped[0];
		if (!target) {
			setReady(true);
			return;
		}

		let cancelled = false;
		setReady(false);
		setError(null);

		void (async () => {
			try {
				if (organisationId !== target.organisationId) {
					const result = await api.selectWorkspace(target.organisationId);
					setOrganisationId(result.organisationId);
					await queryClient.invalidateQueries();
				}
				if (!cancelled) setReady(true);
			} catch (caught) {
				if (!cancelled) setError(presentError(caught).title);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [activeId, organisationId, queryClient, scoped, setOrganisationId]);

	async function pick(nextId: string) {
		setError(null);
		setReady(false);
		try {
			const result = await api.selectWorkspace(nextId);
			setOrganisationId(result.organisationId);
			await queryClient.invalidateQueries();
			setReady(true);
		} catch (caught) {
			setError(presentError(caught).title);
		}
	}

	if (scoped.length === 0) {
		return (
			<div className="card">
				<h2>{emptyTitle}</h2>
				<p className="sub">{emptyDetail}</p>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-6">
			{scoped.length > 1 ? (
				<nav aria-label="Workspace" className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
					<ul className="flex min-w-max gap-1 border-b border-(--border-subtle)">
						{scoped.map((membership) => {
							const active = membership.organisationId === activeId;
							return (
								<li key={membership.organisationId}>
									<button
										type="button"
										aria-current={active ? "page" : undefined}
										className={cn(
											"-mb-px inline-flex h-10 items-center border-b-2 px-3 text-sm font-medium transition-colors",
											active
												? "border-(--color-brand-600) text-(--text-primary)"
												: "border-transparent text-(--text-secondary) hover:text-(--text-primary)",
										)}
										onClick={() => void pick(membership.organisationId)}
									>
										{membership.organisationName}
									</button>
								</li>
							);
						})}
					</ul>
				</nav>
			) : null}
			{error && <p className="err">{error}</p>}
			{ready ? children : <p className="empty">Loading workspace.</p>}
		</div>
	);
}
